import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Users, CalendarCheck, MessageSquare, MessageCircle, RefreshCw, Mail, Phone, Briefcase } from "lucide-react";
import { api, authHeader, EDX } from "@/lib/api";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState<"bookings" | "contacts" | "careers">("bookings");
  const [stats, setStats] = useState<any>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [careers, setCareers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, b, c, cr] = await Promise.all([
        api.get("/admin/stats", { headers: authHeader() }),
        api.get("/admin/bookings", { headers: authHeader() }),
        api.get("/admin/contacts", { headers: authHeader() }),
        api.get("/admin/careers", { headers: authHeader() }).catch(() => ({ data: [] })),
      ]);
      setStats(s.data); setBookings(b.data); setContacts(c.data); setCareers(cr.data);
    } catch (e: any) {
      if (e?.response?.status === 401) nav("/admin");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem("edx_admin_token")) { nav("/admin"); return; }
    fetchAll(); // eslint-disable-next-line
  }, []);

  const logout = () => { localStorage.removeItem("edx_admin_token"); localStorage.removeItem("edx_admin_email"); nav("/admin"); };

  return (
    <main className="min-h-screen bg-edx-space relative" data-testid="admin-dashboard">
      <div className="absolute inset-0 edx-grid opacity-20" />
      <header className="relative border-b border-white/5 bg-edx-eclipse/40 backdrop-blur-xl sticky top-0 z-30">
        <div className="container-edx py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={EDX.logo} alt="EDENOX" className="h-9 w-9 rounded-lg" />
            <div>
              <div className="font-display font-bold text-white text-sm">EDENOX Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-edx-muted font-mono">{localStorage.getItem("edx_admin_email")}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={fetchAll} className="btn btn-ghost !py-2 !px-3 !text-xs">
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="btn btn-ghost !py-2 !px-3 !text-xs">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="relative container-edx py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-stats">
          <Stat icon={<CalendarCheck size={18} />} label="Bookings" value={stats?.bookings_total ?? "–"} sub={`${stats?.bookings_last_7d ?? 0} this week`} color="cyan" />
          <Stat icon={<Users size={18} />} label="Contacts" value={stats?.contacts_total ?? "–"} sub={`${stats?.contacts_last_7d ?? 0} this week`} color="pink" />
          <Stat icon={<MessageSquare size={18} />} label="Chat sessions" value={stats?.chat_sessions_total ?? "–"} sub="NOVA chats" color="purple" />
          <Stat icon={<MessageCircle size={18} />} label="Chat messages" value={stats?.chat_messages_total ?? "–"} sub="Exchanges" color="orange" />
          <Stat icon={<Briefcase size={18} />} label="Applications" value={stats?.careers_total ?? "–"} sub="Careers" color="green" />
        </div>

        <div className="mt-10 flex gap-2 border-b border-white/5">
          {[["bookings", `Bookings (${bookings.length})`], ["contacts", `Contacts (${contacts.length})`], ["careers", `Careers (${careers.length})`]].map(([k, l]) => (
            <button key={k as string} data-testid={`tab-${k}`} onClick={() => setTab(k as any)}
              className={`px-4 py-3 text-sm font-display font-semibold border-b-2 -mb-px transition-all ${tab === k ? "border-edx-pink text-white" : "border-transparent text-edx-muted hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {loading ? <div className="text-edx-muted p-10 text-center">Loading…</div> :
            tab === "bookings" ? <BookingsT rows={bookings} /> :
            tab === "contacts" ? <ContactsT rows={contacts} /> :
            <CareersT rows={careers} />}
        </div>
      </div>
    </main>
  );
}

function Stat({ icon, label, value, sub, color }: any) {
  const c: any = { cyan: "bg-edx-cyan/10 text-edx-cyan border-edx-cyan/30", pink: "bg-edx-pink/10 text-edx-pink border-edx-pink/30", purple: "bg-edx-purple/10 text-edx-purple border-edx-purple/30", orange: "bg-edx-orange/10 text-edx-orange border-edx-orange/30", green: "bg-edx-green/10 text-edx-green border-edx-green/30" };
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass p-5">
      <div className={`inline-flex h-10 w-10 rounded-lg items-center justify-center border ${c[color]}`}>{icon}</div>
      <div className="mt-3 text-[10px] uppercase tracking-widest text-edx-muted font-mono">{label}</div>
      <div className="mt-1 display font-extrabold text-3xl text-white">{value}</div>
      <div className="text-[11px] text-edx-muted">{sub}</div>
    </motion.div>
  );
}

function BookingsT({ rows }: any) {
  if (!rows.length) return <div className="glass p-12 text-center text-edx-muted">No bookings yet.</div>;
  return (
    <div className="glass overflow-x-auto" data-testid="bookings-table">
      <table className="w-full text-sm">
        <thead className="bg-white/5"><tr className="text-left text-[10px] uppercase tracking-widest text-edx-muted font-mono">
          <Th>Name</Th><Th>Email</Th><Th>Service</Th><Th>Package</Th><Th>Budget</Th><Th>Date</Th><Th>Created</Th>
        </tr></thead>
        <tbody>{rows.map((r: any) => (
          <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]">
            <Td><div className="font-semibold text-white">{r.name}</div>{r.company && <div className="text-[11px] text-edx-muted">{r.company}</div>}</Td>
            <Td><a href={`mailto:${r.email}`} className="text-edx-cyan hover:text-edx-pink flex items-center gap-1"><Mail size={11} /> {r.email}</a>{r.phone && <div className="text-[11px] text-edx-muted flex items-center gap-1 mt-0.5"><Phone size={10} /> {r.phone}</div>}</Td>
            <Td>{r.service_category}</Td><Td>{r.package_tier || "–"}</Td><Td>{r.budget_range || "–"}</Td>
            <Td>{r.preferred_date || "–"} {r.preferred_time}</Td>
            <Td className="whitespace-nowrap text-[11px] text-edx-muted font-mono">{new Date(r.created_at).toLocaleString()}</Td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function ContactsT({ rows }: any) {
  if (!rows.length) return <div className="glass p-12 text-center text-edx-muted">No contact messages yet.</div>;
  return (
    <div className="glass overflow-x-auto" data-testid="contacts-table">
      <table className="w-full text-sm">
        <thead className="bg-white/5"><tr className="text-left text-[10px] uppercase tracking-widest text-edx-muted font-mono">
          <Th>Name</Th><Th>Email</Th><Th>Subject</Th><Th>Message</Th><Th>Created</Th>
        </tr></thead>
        <tbody>{rows.map((r: any) => (
          <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]">
            <Td><div className="font-semibold text-white">{r.name}</div>{r.company && <div className="text-[11px] text-edx-muted">{r.company}</div>}</Td>
            <Td><a href={`mailto:${r.email}`} className="text-edx-cyan hover:text-edx-pink">{r.email}</a></Td>
            <Td>{r.subject || "–"}</Td>
            <Td className="max-w-md"><div className="text-edx-muted line-clamp-3">{r.message}</div></Td>
            <Td className="whitespace-nowrap text-[11px] text-edx-muted font-mono">{new Date(r.created_at).toLocaleString()}</Td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function CareersT({ rows }: any) {
  if (!rows.length) return <div className="glass p-12 text-center text-edx-muted">No applications yet.</div>;
  return (
    <div className="glass overflow-x-auto" data-testid="careers-table">
      <table className="w-full text-sm">
        <thead className="bg-white/5"><tr className="text-left text-[10px] uppercase tracking-widest text-edx-muted font-mono">
          <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Portfolio</Th><Th>CV</Th><Th>Created</Th>
        </tr></thead>
        <tbody>{rows.map((r: any) => (
          <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]">
            <Td><div className="font-semibold text-white">{r.name}</div></Td>
            <Td><a href={`mailto:${r.email}`} className="text-edx-cyan hover:text-edx-pink">{r.email}</a></Td>
            <Td>{r.role}</Td>
            <Td>{r.portfolio_url ? <a href={r.portfolio_url} target="_blank" rel="noreferrer" className="text-edx-cyan">link</a> : "–"}</Td>
            <Td>{r.cv_url ? <a href={r.cv_url} target="_blank" rel="noreferrer" className="text-edx-cyan">link</a> : "–"}</Td>
            <Td className="whitespace-nowrap text-[11px] text-edx-muted font-mono">{new Date(r.created_at).toLocaleString()}</Td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

const Th = ({ children }: any) => <th className="px-4 py-3 font-semibold">{children}</th>;
const Td = ({ children, className = "" }: any) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
